import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, GripVertical, Type, AlignLeft, Hash, ToggleLeft, ListChecks, Calendar as CalIcon, Upload, Star } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const TYPE_META = {
  text: { label: "Short text", Icon: Type },
  textarea: { label: "Long text", Icon: AlignLeft },
  number: { label: "Number", Icon: Hash },
  yesno: { label: "Yes / No", Icon: ToggleLeft },
  select: { label: "Single choice", Icon: ListChecks },
  multiselect: { label: "Multi-select", Icon: ListChecks },
  date: { label: "Date", Icon: CalIcon },
  file: { label: "File upload", Icon: Upload },
  rating: { label: "Rating 1–5", Icon: Star },
};
const NEEDS_OPTIONS = ["select", "multiselect"];

export default function AuditionFieldsDialog({ open, onOpenChange, isAdmin }) {
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState({ label: "", type: "text", required: false, options: [], help_text: "" });
  const [optDraft, setOptDraft] = useState("");
  const [saving, setSaving] = useState(false);
  // Drag-and-drop state — org_admin only. `dragId` is the field being
  // dragged; `overId` is the row we're hovering, drawn as a visual guide.
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/audition-fields"); setFields(data); }
    catch { toast.error("Could not load fields"); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const reset = () => { setForm({ label: "", type: "text", required: false, options: [], help_text: "" }); setOptDraft(""); };

  const create = async () => {
    if (!form.label.trim()) return toast.error("Label is required");
    if (NEEDS_OPTIONS.includes(form.type) && form.options.length === 0) return toast.error("Add at least one option");
    setSaving(true);
    try { await api.post("/audition-fields", form); toast.success("Field added"); reset(); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add field"); }
    finally { setSaving(false); }
  };
  const remove = async (f) => {
    if (!confirm(`Remove "${f.label}"? Existing answers on this field will remain in the audition record but stop rendering.`)) return;
    try { await api.delete(`/audition-fields/${f.id}`); toast.success("Removed"); await load(); }
    catch { toast.error("Could not remove"); }
  };
  const toggleRequired = async (f) => {
    try { await api.patch(`/audition-fields/${f.id}`, { required: !f.required }); await load(); }
    catch { toast.error("Could not update"); }
  };
  const addOption = () => {
    const v = optDraft.trim(); if (!v) return;
    if (form.options.includes(v)) return;
    setForm((f) => ({ ...f, options: [...f.options, v] })); setOptDraft("");
  };

  // Optimistic reorder — we drop the dragged row above the target row,
  // patch the local state immediately for zero-lag feedback, then persist
  // the new order. On failure we reload from the server to snap back.
  const commitOrder = async (nextIds) => {
    const previous = fields;
    const nextFields = nextIds.map((id) => previous.find((f) => f.id === id)).filter(Boolean);
    setFields(nextFields);
    try {
      await api.post("/audition-fields/reorder", { field_ids: nextIds });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save new order");
      setFields(previous);
    }
  };
  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox refuses to fire dragover unless we set some data.
    try { e.dataTransfer.setData("text/plain", id); } catch { /* ignore */ }
  };
  const onDragOver = (id) => (e) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    setOverId(id);
  };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const ids = fields.map((f) => f.id).filter((id) => id !== dragId);
    const idx = ids.indexOf(targetId);
    ids.splice(idx, 0, dragId);
    setDragId(null); setOverId(null);
    commitOrder(ids);
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="fields-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ListChecks className="w-5 h-5 text-gold" /> Custom audition questions</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Additional questions appear at the end of the public audition form and on every audition profile.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 pr-1 -mr-1">
          <section>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center justify-between">
              <span>Current questions ({fields.length})</span>
              {isAdmin && fields.length > 1 && (
                <span className="text-[10px] normal-case tracking-normal text-zinc-500">Drag <GripVertical className="inline w-3 h-3 -mt-0.5" /> to reorder</span>
              )}
            </div>
            {fields.length === 0 ? (
              <div className="rounded border border-dashed border-white/10 p-8 text-center text-xs text-zinc-500 bg-[#0F0F0F]">No custom questions yet.</div>
            ) : (
              <ul className="rounded border border-white/10 divide-y divide-white/5" data-testid="fields-list">
                {fields.map((f) => {
                  const M = TYPE_META[f.type] || TYPE_META.text;
                  const isDragging = dragId === f.id;
                  const isOver = overId === f.id && dragId && dragId !== f.id;
                  return (
                    <li
                      key={f.id}
                      data-testid={`field-row-${f.id}`}
                      draggable={isAdmin}
                      onDragStart={isAdmin ? onDragStart(f.id) : undefined}
                      onDragOver={isAdmin ? onDragOver(f.id) : undefined}
                      onDrop={isAdmin ? onDrop(f.id) : undefined}
                      onDragEnd={isAdmin ? onDragEnd : undefined}
                      className={`px-3 py-2 flex items-center gap-3 text-sm transition-all
                        ${isDragging ? "opacity-40" : ""}
                        ${isOver ? "border-t-2 border-t-gold" : ""}
                        ${isAdmin ? "cursor-move" : ""}`}
                    >
                      <GripVertical
                        data-testid={`field-grip-${f.id}`}
                        className={`w-3.5 h-3.5 shrink-0 ${isAdmin ? "text-zinc-400" : "text-zinc-700"}`}
                      />
                      <M.Icon className="w-4 h-4 text-zinc-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{f.label}</div>
                        <div className="text-[10px] text-zinc-500">{M.label}{f.options?.length ? ` · ${f.options.join(", ")}` : ""}</div>
                      </div>
                      {isAdmin && (
                        <>
                          <label className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400">
                            <Switch checked={f.required} onCheckedChange={() => toggleRequired(f)} data-testid={`req-${f.id}`} /> Req
                          </label>
                          <button data-testid={`del-field-${f.id}`} onClick={() => remove(f)} className="h-7 w-7 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 grid place-items-center"><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {isAdmin && (
            <section className="rounded-lg border border-gold/30 bg-gold/5 p-4 space-y-3" data-testid="add-field">
              <div className="text-[10px] uppercase tracking-widest text-gold">Add a question</div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-2">
                <Input data-testid="field-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Question label" className="bg-[#0A0A0A] border-white/10 h-10" />
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, options: [] })}>
                  <SelectTrigger data-testid="field-type" className="bg-[#0A0A0A] border-white/10 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input value={form.help_text} onChange={(e) => setForm({ ...form, help_text: e.target.value })} placeholder="Optional help text shown under the label" className="bg-[#0A0A0A] border-white/10 h-10 text-sm" />
              {NEEDS_OPTIONS.includes(form.type) && (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Options</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Input value={optDraft} onChange={(e) => setOptDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                      placeholder="Type & press Add" className="bg-[#0A0A0A] border-white/10 h-9 text-sm" data-testid="field-option-input" />
                    <button type="button" onClick={addOption} className="h-9 px-3 rounded border border-white/15 text-xs">Add</button>
                  </div>
                  {form.options.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {form.options.map((o) => (
                        <span key={o} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/30">{o}<button type="button" onClick={() => setForm({ ...form, options: form.options.filter((x) => x !== o) })} className="ml-1 text-gold/70 hover:text-gold">×</button></span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-xs text-zinc-300">
                  <Switch checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: v })} data-testid="field-required" /> Required
                </label>
                <button data-testid="add-field-btn" onClick={create} disabled={saving} style={goldBtn} className="ml-auto h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> {saving ? "Adding…" : "Add question"}</button>
              </div>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
